import { format } from "date-fns";
import { useCallback, useState } from "react";
import { DateSelector } from "../components/DateSelector";
import { SeatDetailModal, SeatMap } from "../components/SeatMap";
import {
  PageContainer,
  PageContent,
  PageHeader,
} from "../components/layout/PageHeader";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { ConfirmModal } from "../components/ui/Modal";
import type { Reservation } from "../hooks/api/useReservations";
import { useCancelReservation } from "../hooks/api/useReservations";
import { useSeats } from "../hooks/api/useSeats";
import { useDateStore } from "../stores/dateStore";
import { useToastStore } from "../stores/toastStore";

interface SelectedSeatInfo {
  seatId: number;
  seatLabel: string;
  reservation?: Reservation;
}

interface CancelConfirmTarget {
  seatId: number;
  seatLabel: string;
  reservation: Reservation;
}

export function SeatsPage() {
  const { selectedDate } = useDateStore();
  const dateString = format(selectedDate, "yyyy-MM-dd");
  const { data: seats } = useSeats();
  const cancelReservation = useCancelReservation();
  const addToast = useToastStore((s) => s.addToast);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSeatInfo, setSelectedSeatInfo] =
    useState<SelectedSeatInfo | null>(null);

  const [confirmTarget, setConfirmTarget] =
    useState<CancelConfirmTarget | null>(null);

  const getSeatLabel = useCallback(
    (seatId: number): string => {
      const seat = seats?.find((s) => s.seatId === seatId);
      return seat?.area ? `座席 ${seatId}（${seat.area}）` : `座席 ${seatId}`;
    },
    [seats]
  );

  const handleSeatClick = useCallback(
    (seatId: number, reservation?: Reservation) => {
      setSelectedSeatInfo({
        seatId,
        seatLabel: getSeatLabel(seatId),
        reservation,
      });
      setIsModalOpen(true);
    },
    [getSeatLabel]
  );

  const handleSeatDoubleClick = useCallback(
    (seatId: number, reservation: Reservation) => {
      // タイマーでシングルクリックは抑止されるが、防御的にSeatDetailModalも閉じる
      setIsModalOpen(false);
      setSelectedSeatInfo(null);
      setConfirmTarget({
        seatId,
        seatLabel: getSeatLabel(seatId),
        reservation,
      });
    },
    [getSeatLabel]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedSeatInfo(null);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setConfirmTarget(null);
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!confirmTarget) {
      return;
    }
    try {
      await cancelReservation.mutateAsync({
        date: dateString,
        seatId: confirmTarget.seatId,
      });
      addToast(`${confirmTarget.seatLabel}の予約をキャンセルしました`);
      handleConfirmClose();
    } catch {
      // モーダルは閉じずに残し、ユーザーが再試行できるようにする
      addToast("キャンセルに失敗しました");
    }
  }, [
    confirmTarget,
    cancelReservation,
    dateString,
    addToast,
    handleConfirmClose,
  ]);

  return (
    <PageContainer>
      <PageHeader />
      <PageContent>
        <h1 className="sr-only">座席マップ</h1>
        <div className="space-y-6">
          <DateSelector />

          <Card>
            <CardHeader title="座席状況" />
            <CardContent>
              <SeatMap
                date={dateString}
                onSeatClick={handleSeatClick}
                onSeatDoubleClick={handleSeatDoubleClick}
              />
            </CardContent>
          </Card>
        </div>
      </PageContent>

      {selectedSeatInfo && (
        <SeatDetailModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          seatId={selectedSeatInfo.seatId}
          seatLabel={selectedSeatInfo.seatLabel}
          date={dateString}
          reservation={selectedSeatInfo.reservation}
        />
      )}

      {confirmTarget && (
        <ConfirmModal
          isOpen={confirmTarget !== null}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmCancel}
          title="予約キャンセル"
          message={`${confirmTarget.seatLabel}の予約をキャンセルしますか？`}
          confirmText="キャンセルする"
          cancelText="戻る"
          variant="danger"
          isLoading={cancelReservation.isPending}
        />
      )}
    </PageContainer>
  );
}
